import Alert from "components/common/Alert";
import { useEffect, useRef, useMemo, useState } from "react";
import { getExhibitor } from "api/exhibitor.api";
import { getImageUrl } from "utils/imageHelper";
import { getProductByExhibitor } from "api/product.api";
import Select from "react-select";

export default function OfferForm({
  initialData = null,
  onSubmit,
  submitLabel,
  onBack,
}) {
  const [exhibitors, setExhibitors] = useState([]);
  const [products, setProducts] = useState([]);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const alertRef = useRef(null);

  const normalizePath = (p) => {
    if (typeof p !== "string") return "";
    return p.replace(/\\/g, "/");
  };

  const parseDefaultImage = (val) => {
  if (!val) return "";
  if (typeof val === "string" && val.startsWith("{")) return val;
  return JSON.stringify({
    type: "existing",
    path: normalizePath(val),
  });
};


  const [formData, setFormData] = useState({
  company_id: "",
  exhibitor_id: "",
  target_products: [],
  title: "",
  description: "",
  tc: "",
  promo_code: "",
  discount: "",
  start_date: "",
  end_date: "",
  status: "inactive",

  offerImages: [],
  existingImages: [],
  default_image: null,
  removed_images: [],
});

const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "48px",
      height: "48px",
      borderRadius: "0.75rem",
      borderColor: state.isFocused ? "#c7d2fe" : "#d1d5db",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#a5b4fc",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      height: "48px",
      padding: "0 12px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "48px",
    }),
  };




  useEffect(() => {
  if (!initialData?._id) return;

  setFormData({
    company_id: initialData.company_id?._id || "",
    exhibitor_id: initialData.exhibitor_id?._id || "",
    target_products: Array.isArray(initialData.target_products)
      ? initialData.target_products.map(p =>
          typeof p === "string" ? p : p._id
        )
      : [],
    title: initialData.title || "",
    description: initialData.description || "",
    tc: initialData.tc || "",
    promo_code: initialData.promo_code || "",
    discount: initialData.discount || "",
    start_date: initialData.start_date?.slice(0, 16) || "",
    end_date: initialData.end_date?.slice(0, 16) || "",
    status: initialData.status || "inactive",

    offerImages: [],
    existingImages: initialData.existingImages || [],
    removed_images: [],
    default_image: parseDefaultImage(initialData.default_image)
  ? JSON.stringify({
      type: "existing",
      path: normalizePath(initialData.default_image),
    })
  : "",

  });
}, [initialData]);



  useEffect(() => {
    const companyId = localStorage.getItem("company_id");
    if (!companyId) return;

    getExhibitor(companyId)
      .then(res => setExhibitors(res.data || []))
      .catch(() => setExhibitors([]));
  }, []);

  useEffect(() => {
  if (!formData.exhibitor_id) {
    setProducts([]);
    return;
  }

  getProductByExhibitor(formData.exhibitor_id)
    .then(res => setProducts(res.data.data || []))
    .catch(() => setProducts([]));
}, [formData.exhibitor_id]);


  useEffect(() => {
    if (alert && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [alert]);

    const productOptions = useMemo(
    () =>
      products.map(p => ({
        value: p._id,
        label: p.product_name,
      })),
    [products]
  );

  const selectedProducts = useMemo(() => {
    if (!productOptions.length || !formData.target_products?.length) return [];
    return productOptions.filter(opt =>
      formData.target_products.includes(opt.value)
    );
  }, [productOptions, formData.target_products]);


  const handleImages = (e) => {
    const files = Array.from(e.target.files);
  if (!files.length) return;

  setFormData(prev => {
    const hasDefault = !!prev.default_image;

    const defaultImage = hasDefault
      ? prev.default_image
      : JSON.stringify({
          type: "new",
          index: prev.offerImages.length, 
        });

    return {
      ...prev,
      offerImages: [...prev.offerImages, ...files],
      default_image: defaultImage,
    };
  });
  };

const setDefaultImage = (img, index) => {
  if (typeof img === "string") {
    setFormData(p => ({
      ...p,
      default_image: JSON.stringify({
        type: "existing",
        path: normalizePath(img),
      }),
    }));
  } else {
    setFormData(p => ({
      ...p,
      default_image: JSON.stringify({
        type: "new",
        index,
      }),
    }));
  }
};



  const removeImage = (index, isExisting) => {
  setFormData(p => {
    if (isExisting) {
      const img = p.existingImages[index];
      return {
        ...p,
        existingImages: p.existingImages.filter((_, i) => i !== index),
        removed_images: [...p.removed_images, img],
        default_image: initialData.default_image
          ? typeof initialData.default_image === "string" &&
            initialData.default_image.startsWith("{")
              ? initialData.default_image 
              : JSON.stringify({
                  type: "existing",
                  path: normalizePath(initialData.default_image),
                })
          : "",

      };
    }

    const newIndex = index - p.existingImages.length;
    const imgs = [...p.offerImages];
    const removed = imgs.splice(newIndex, 1)[0];

    return {
      ...p,
      offerImages: imgs,
      default_image:
        defaultMeta?.index === newIndex ? "" : p.default_image,
    };
  });
};



  const validate = () => {
    const e = {};
    if (!formData.exhibitor_id) e.exhibitor_id = "Exhibitor is required";
    if (!formData.target_products.length)
      e.target_products = "Select at least one product";
    if (!formData.title?.trim()) e.title = "Offer title is required";
    if (!formData.description?.trim()) e.description = "Description is required";
    if (!formData.start_date) e.start_date = "Start date is required";
    if (!formData.end_date) e.end_date = "End date is required";
    if (!formData.status) e.status = "Status is required";
    return e;
  };

  const handleChange = (e) => {
  const { name, value } = e.target;
  setFormData((p) => ({
    ...p,
    [name]: value,
  }));
};

const defaultMeta = useMemo(() => {
  try {
    return formData.default_image
      ? JSON.parse(formData.default_image)
      : null;
  } catch {
    return null;
  }
}, [formData.default_image]);



  const submit = async (e) => {
    e.preventDefault();

    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      setAlert("Please fix the errors below");
      return;
    }

    setErrors({});
    setAlert("");

    const fd = new FormData();

    Object.entries(formData).forEach(([k, v]) => {
    if (
        ["target_products", "offerImages", "existingImages", "default_image"].includes(k)
    ) return;
    if (v !== "" && v !== null) fd.append(k, v);
    });

    formData.target_products.forEach(id =>
    fd.append("target_products[]", id)
    );

    formData.offerImages.forEach(file =>
    fd.append("offerImages", file)
    );

    if (formData.default_image) {
    fd.append("default_image", formData.default_image);
    }

    if (formData.removed_images.length) {
    fd.append("removed_images", JSON.stringify(formData.removed_images));
    }


    setSubmitting(true);
    try {
      await onSubmit(fd);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-10">
      {alert && (
        <div ref={alertRef}>
          <Alert type="error" message={alert} onClose={() => setAlert("")} />
        </div>
      )}

      <Section title="Offer Details">
        <SelectInput
          label="Exhibitor"
          required
          value={formData.exhibitor_id}
          error={errors.exhibitor_id}
          onChange={(e) =>
            setFormData(p => ({ ...p, exhibitor_id: e.target.value }))
          }
        >
          <option value="">Select Exhibitor</option>
          {exhibitors.map(e => (
            <option key={e._id} value={e._id}>{e.title}</option>
          ))}
        </SelectInput>
          <div>
            <label className="mb-1 block text-sm font-medium">
              Target Product<span className="text-red-500">*</span>
            </label> 
            <Select
            isMulti
            options={productOptions}
            value={selectedProducts}
            onChange={(selected) =>
              setFormData(p => ({
                ...p,
                target_products: selected.map(s => s.value),
              }))
            }
            classNamePrefix="react-select"
            styles={selectStyles}
          />
           {errors.target_products && (
                          <p className="text-xs text-red-500">{errors.target_products}</p>
                        )}

          </div>

        <Input
          label="Offer Title"
          required
          value={formData.title}
          error={errors.title}
          onChange={(e) =>
            setFormData(p => ({ ...p, title: e.target.value }))
          }
        />

        <Textarea
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            error={errors.description}
            />

        <Input
            label="Promo Code (If Any)"
            name="promo_code"
            value={formData.promo_code}
            onChange={handleChange}
            />

            <Input
            label="Discount"
            name="discount"
            value={formData.discount}
            onChange={handleChange}
            />

            <Input
            type="datetime-local"
            label="Start Date"
            name="start_date"
            value={formData.start_date}
            onChange={handleChange}
            required
            error={errors.start_date}
            />

            <Input
            type="datetime-local"
            label="End Date"
            name="end_date"
            value={formData.end_date}
            onChange={handleChange}
            required
            error={errors.end_date}
            />

            

            <Textarea
            label="Terms & Conditions"
            name="tc"
            value={formData.tc}
            onChange={handleChange}
            />
             <SelectInput
            label="Available Status"
            value={formData.status}
            onChange={(e) =>
                setFormData(p => ({ ...p, status: e.target.value }))
            }
            required
            errors={errors.status}
            >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            </SelectInput>
        </Section>
         <Section title="Offer Images">
                <div className="md:col-span-2 space-y-3">
                <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImages}
                    className="w-full rounded-xl border p-3"
                />

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...formData.existingImages, ...formData.offerImages].map((img, i) => {
                    const isExisting = typeof img === "string";

                    const src = isExisting
                    ? getImageUrl(img)
                    : URL.createObjectURL(img);

                   const newIndex = i - formData.existingImages.length;

                    const isDefault =
                      (defaultMeta?.type === "existing" &&
                        normalizePath(defaultMeta.path) === normalizePath(img)) ||
                      (defaultMeta?.type === "new" &&
                        !isExisting &&
                        defaultMeta.index === newIndex);

                    return (
                    <div
                        key={i}
                        className={`relative border rounded-xl p-2 ${
                        isDefault ? "ring-2 ring-brand-500" : ""
                        }`}
                    >
                        <img
                        src={src}
                        className="h-32 w-full object-cover rounded-lg"
                        />

                        <div className="mt-2 flex justify-between">
                          {isDefault ? (
                            <span className="text-xs font-medium text-green-600">
                              Default Image
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                setDefaultImage(
                                  img,
                                  isExisting ? null : i - formData.existingImages.length
                                )
                              }
                              className="text-xs text-blue-600"
                            >
                              Set Default
                            </button>
                          )}


                          <button
                            type="button"
                            onClick={() => removeImage(i, isExisting)}
                            className="text-xs text-red-600"
                          >
                            Remove
                          </button>
                        </div>

                    </div>
                    );
                })}
                </div>

                </div>
            </Section>

        

      <div className="flex justify-between">
        {onBack && (
          <button type="button" onClick={onBack} className="rounded-xl bg-red-500 px-6 py-3 text-white">
            Back
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-brand-500 px-6 py-3 text-white"
        >
          {submitLabel || (initialData ? "Update Offer" : "Create Offer")}
        </button>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-4 text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </section>
  );
}

function Input({ label, required, error, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        {...props}
        className={`w-full rounded-xl border p-3 ${
          error ? "border-red-500" : ""
        }`}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function Textarea({ label, required, error, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <textarea
        {...props}
        className={`w-full rounded-xl border p-3 ${
          error ? "border-red-500" : ""
        }`}
      />

      {error && (
        <p className="mt-1 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}

function SelectInput({ label, required, error, children, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        {...props}
        className={`w-full rounded-xl border p-3 ${
          error ? "border-red-500" : ""
        }`}
      >
        {children}
      </select>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
